import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { squadPlayerApi } from '../../services/footballApi';

interface SquadPlayer {
  id: number;
  squad?: { id: number; team?: { name: string }; league?: { name: string } } | null;
  player?: { name: string } | null;
  position?: string;
  status?: string;
  credits?: number;
  points?: number;
  created_at?: string;
  updated_at?: string;
}

const FootballSquadPlayers: React.FC = () => {
  const [squadPlayers, setSquadPlayers] = useState<SquadPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchSquadPlayers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await squadPlayerApi.getAll();
      setSquadPlayers(res.data);
    } catch (err: any) {
      setError('Failed to fetch squad players');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSquadPlayers();
  }, [fetchSquadPlayers]);

  const handleEdit = (id: number) => {
    navigate(`/football/squad-players/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this squad player?')) return;
    try {
      await squadPlayerApi.delete(id);
      fetchSquadPlayers();
    } catch (err) {
      alert('Failed to delete squad player');
    }
  };

  const handleCreate = () => {
    navigate('/football/squad-players/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'squad', headerName: 'Squad', flex: 1, minWidth: 160, valueGetter: (value: SquadPlayer['squad']) => value ? `${value.team?.name || ''} (${value.league?.name || ''})` : '' },
    { field: 'player', headerName: 'Player', flex: 1, minWidth: 120, valueGetter: (value: SquadPlayer['player']) => value?.name || '' },
    { field: 'position', headerName: 'Position', width: 100 },
    { field: 'status', headerName: 'Status', width: 100 },
    { field: 'credits', headerName: 'Credits', width: 100, type: 'number' },
    { field: 'points', headerName: 'Points', width: 100, type: 'number' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: SquadPlayer }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Squad Players</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Squad Player
        </Button>
      </Stack>
      <DataGrid
        rows={squadPlayers}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballSquadPlayers; 